import { useEffect, useState } from 'react';
import { formatDate } from '../utils/dateUtils';
import axios from 'axios';
import BottomNav from '../components/BottomNav';
import { linkify } from '../utils/linkify';
import useAutoRefresh from '../utils/refresh';

export default function Schedule() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkinModal, setCheckinModal] = useState(null); // shift object
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkedIn, setCheckedIn] = useState({}); // {event_id: true}
  // Session AI: the shift whose detail sheet is open, or null.
  const [detailShift, setDetailShift] = useState(null);
  // Refetch when the app returns to the foreground, when BottomNav's poll
  // sees a counter rise, or when the active tab is tapped. See utils/refresh.js.
  const refreshKey = useAutoRefresh();

  useEffect(() => {
    axios.get('/api/staff/schedule')
      .then(r => {
        const now = new Date();
        const upcoming = r.data.filter(s => {
          try {
            const start = s.time_start || '00:00';
            const endT = s.time_end || '23:59';
            // Session W (W4): the explicit +02:00 offset matters. Without it the
            // browser parses the shift time in the PHONE's timezone while the
            // server treats the same stored string as SAST, so a handset set to
            // the wrong timezone (or genuinely travelling) filtered shifts in and
            // out of this list on a different clock to the one the server uses.
            const end = new Date(`${s.date}T${endT}:00+02:00`);
            const [sh, sm] = start.split(':').map(Number);
            const [eh, em] = endT.split(':').map(Number);
            if ((eh * 60 + em) < (sh * 60 + sm)) end.setDate(end.getDate() + 1); // overnight — ends next day
            return end >= now;
          } catch { return true; }
        });
        setShifts(upcoming);
        // Seed already-checked-in state from server data
        const ci = {};
        upcoming.forEach(s => { if (s.checked_in) ci[s.id] = true; });
        setCheckedIn(ci);
        axios.post('/api/staff/schedule-seen').catch(() => {});
      })
      .finally(() => setLoading(false));
    // ⚠️ This effect posts /api/staff/schedule-seen, so every refresh clears
    // the Schedule badge. That is why BottomNav emits on a counter RISING
    // only — on 'any change' the 1 → 0 this causes would emit straight back.
  }, [refreshKey]);

  // ── Session AI: detail sheet open/close ──────────────────────────────────
  // Everything /api/staff/schedule returns is ALREADY here — it is `SELECT e.*`
  // and Next Shift renders description and dress_code from the very same call.
  // This screen simply never showed them, so an agent could edit a brief, the
  // update notification would fire, and the staff member had nowhere to read
  // what changed. No server change was needed.
  //
  // ⚠️ The history entry exists so ANDROID BACK closes the sheet instead of
  // leaving the app — the natural escape from a mis-tap, and half the staff are
  // on Android. Scoped deliberately: the URL never changes (react-router
  // re-resolves /schedule to itself, a no-op), the listener only exists while
  // the sheet is open, and closing always goes through history.back() so there
  // is ONE close path rather than two that can disagree.
  const openDetail = (shift) => {
    setDetailShift(shift);
    try { window.history.pushState({ esSheet: true }, ''); } catch (e) { /* no-op */ }
  };
  const closeDetail = () => {
    // Only rewind if our own entry is on top; otherwise close directly, so a
    // failed pushState can never send the person back a real page.
    if (window.history.state && window.history.state.esSheet) window.history.back();
    else setDetailShift(null);
  };
  useEffect(() => {
    if (!detailShift) return undefined;
    const onPop = () => setDetailShift(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [detailShift]);

  const withinCheckinWindow = (shift) => {
    // Session W (W4): +02:00 to match the server. POST /api/shifts/:id/checkin
    // builds the same instant with an explicit SAST offset, so without this a
    // phone on the wrong timezone showed a Check in button the server then
    // rejected — or hid one the person was entitled to press.
    const start = new Date(`${shift.date}T${shift.time_start || '00:00'}:00+02:00`);
    const hoursUntil = (start - new Date()) / (1000 * 60 * 60);
    return hoursUntil <= 24 && hoursUntil > -2;
  };

  // Session M1 tweak: on a day of a multi-day event, a "per event" flat rate
  // is earned PER DAY — display it as "for the day" so staff applying to
  // several days aren't misled into expecting one amount for the whole series.
  const payText = (s) => {
    const t = s.pay_unit === 'per_staff'
      ? (s.applied_rate != null ? `R${Number(s.applied_rate)}/hr` : null)
      : (s.role_pay_rate || s.pay_rate);
    return ((s.series_size || 1) > 1 && s.pay_unit === 'per_event' && t)
      ? String(t).replace('for the event', 'for the day')
      : t;
  };

  const confirmCheckin = async () => {
    if (!checkinModal) return;
    setCheckinLoading(true);
    try {
      await axios.post(`/api/shifts/${checkinModal.event_id || checkinModal.id}/checkin`);
      setCheckedIn(c => ({ ...c, [checkinModal.id]: true }));
      setCheckinModal(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not check in. Please try again.');
    }
    setCheckinLoading(false);
  };


  return (
    <div className="app-shell">
      <div className="screen">
        <div className="topbar">
          <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px' }}>Schedule</h2>
        </div>

        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 14, borderBottom: '1px solid var(--border-light)', marginBottom: 4 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth={1.8} strokeLinecap="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4"/>
            </svg>
            <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.2px', color: 'var(--text-primary)' }}>My Confirmed Shifts</div>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : shifts.length === 0 ? (
            <div className="empty-state">No confirmed shifts yet.<br />Apply for shifts in the Book tab.</div>
          ) : (
            shifts.map(shift => {
              const canCheckin = withinCheckinWindow(shift);
              const alreadyCheckedIn = checkedIn[shift.id] || shift.checked_in;
              return (
                <div
                  key={shift.id}
                  className="shift-card"
                  style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer' }}
                  onClick={() => openDetail(shift)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(shift); } }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div className="shift-info">
                      {/* Session M1 tweak: flex-wrap row — a long title now wraps and the
                          DAY chip drops to its own line instead of colliding with the
                          CONFIRMED badge. */}
                      {/* Session M1 tweak round 2: .shift-name is nowrap+ellipsis in App.css —
                          long titles clipped hard against the CONFIRMED badge. Override to
                          normal wrapping (staff need to read the full event name) with a gap
                          on the header row so wrapped text never touches the badge. */}
                      <div className="shift-name" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip' }}>
                        <span style={{ overflowWrap: 'anywhere' }}>{shift.title}</span>
                        {(shift.series_size || 1) > 1 && <span className="badge badge-series" style={{ fontSize: 9 }}>DAY {shift.day_number}/{shift.series_size}</span>}
                      </div>
                      <div className="shift-meta">
                        {formatDate(shift.date)} | {shift.time_start} – {shift.time_end}
                      </div>
                      <div className="shift-meta">{shift.location}{shift.street_address ? `, ${shift.street_address}` : ''}</div>
                      <div className="shift-role">{shift.role}</div>
                      {payText(shift) && (
                        <div style={{ marginTop: 4 }}>
                          <span className="pay-chip">💰 {payText(shift)}</span>
                        </div>
                      )}
                      {shift.brief_url && (
                        <div style={{ marginTop: 8 }}>
                          {/* ⚠️ stopPropagation: the whole card opens the detail
                              sheet, so every button inside it must swallow its
                              own click or tapping Read Brief also opens the sheet. */}
                          <button onClick={e => { e.stopPropagation(); window.open(shift.brief_url, '_blank', 'noopener,noreferrer'); }} style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', background: 'rgba(var(--accent-rgb),0.07)', border: '1px solid rgba(var(--accent-rgb),0.25)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>📄 Read Brief</button>
                        </div>
                      )}
                    </div>
                    {/* Session AI: the chevron is the affordance for the card
                        being tappable. Deliberately not a separate button — one
                        target per card, and the whole card is it. */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                      <span className="badge badge-confirmed">CONFIRMED</span>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    </div>
                  </div>

                  {/* Session M1 tweak: the agency unpublished this event AFTER this
                      person was confirmed. They're still booked (unpublish only hides
                      the event from Book a Shift), but if the unpublish was really a
                      cancellation-in-progress or a mistake, better they find out from
                      a note than at the venue. */}
                  {shift.published !== 1 && (
                    <div style={{ marginTop: 10, padding: '9px 12px', background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.3)', borderRadius: 8, fontSize: 12, color: '#a16207', lineHeight: 1.5 }}>
                      ⚠ Your agency has taken this shift off the shift board since you were confirmed. You're still booked — but it's worth checking with them that it's going ahead.
                    </div>
                  )}

                  {/* Check-in row — only visible within 24hrs */}
                  {canCheckin && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: alreadyCheckedIn ? '#16a34a' : 'var(--text-secondary)', fontWeight: alreadyCheckedIn ? 600 : 400 }}>
                        {alreadyCheckedIn ? 'Attendance confirmed' : 'Please confirm your attendance'}
                      </span>
                      {alreadyCheckedIn ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 7, background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2.5}><path d="M20 6L9 17l-5-5"/></svg>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>Checked in</span>
                        </div>
                      ) : (
                        <button
                          onClick={e => { e.stopPropagation(); setCheckinModal(shift); }}
                          style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                          Check in
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div style={{ padding: '0 16px' }}>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            *All upcoming confirmed shifts are listed above.
          </p>
        </div>

        {/* ── Session AI: shift detail sheet ──────────────────────────────
            Same bottom-sheet idiom as the check-in modal below, on purpose —
            staff already know the gesture, and it avoids the layout shift an
            expand-in-place row would cause in the list.
            ⚠️ Check-in lives on the CARD only, not in here: two copies of the
            same action are two things that can disagree. */}
        {detailShift && (
          <div
            onClick={e => { if (e.target === e.currentTarget) closeDetail(); }}
            role="dialog"
            aria-modal="true"
            aria-label="Shift details"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 110 }}
          >
            {/* maxHeight + internal scroll: a long description or a very long
                event name must never push the close control off screen, on any
                handset. The header stays put; only the body scrolls. */}
            <div style={{ background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 430, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -4px 30px rgba(0,0,0,0.2)' }}>

              <div style={{ padding: '12px 20px 10px', borderBottom: '1px solid var(--border-light)', flexShrink: 0 }}>
                <div style={{ width: 36, height: 4, background: 'var(--border)', borderRadius: 2, margin: '0 auto 14px' }} />
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                      {detailShift.title}
                    </div>
                    {(detailShift.series_size || 1) > 1 && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 4 }}>
                        Day {detailShift.day_number} of {detailShift.series_size}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={closeDetail}
                    aria-label="Close"
                    style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 17, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 20px 20px' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', marginBottom: 10 }}>{detailShift.role}</div>

                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 12 }}>
                  <div>{formatDate(detailShift.date)}</div>
                  <div>{detailShift.time_start} – {detailShift.time_end}</div>
                </div>

                {(detailShift.location || detailShift.street_address) && (
                  <div style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Full Address</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                      {[detailShift.location, detailShift.street_address].filter(Boolean).join(', ')}
                    </div>
                  </div>
                )}

                {payText(detailShift) && (
                  <div style={{ marginBottom: 12 }}>
                    <span className="pay-chip">💰 {payText(detailShift)}</span>
                  </div>
                )}

                {detailShift.published !== 1 && (
                  <div style={{ margin: '0 0 12px', padding: '9px 12px', background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.3)', borderRadius: 8, fontSize: 12, color: '#a16207', lineHeight: 1.5 }}>
                    ⚠ Your agency has taken this shift off the shift board since you were confirmed. You're still booked — but it's worth checking with them that it's going ahead.
                  </div>
                )}

                {/* Session AJ: `transport` is a field the agent portal offers
                    (Transport / Parking) and the server stores and ships — and
                    until now NO staff screen rendered it anywhere. An agent
                    could type "no parking on site, use the Bram Fischer garage"
                    and nobody would ever see it. */}
                {(detailShift.description || detailShift.transport || detailShift.dress_code || detailShift.brief_url) && (
                  <>
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', margin: '4px 0 8px' }}>Event Brief</div>
                    {detailShift.description && (
                      // whiteSpace pre-wrap keeps the agent's line breaks; the
                      // break rules stop an unbroken string (a pasted URL, a
                      // long word) forcing sideways scroll on a narrow phone.
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 10, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                        {linkify(detailShift.description)}
                      </p>
                    )}
{detailShift.transport && (
                        <div style={{ marginBottom: 10, padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Transport &amp; Parking</div>
                          <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{linkify(detailShift.transport)}</div>
                        </div>
                      )}
                    {detailShift.dress_code && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Dress code:</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{detailShift.dress_code}</span>
                      </div>
                    )}
                    {detailShift.brief_url && (
                      <button
                        onClick={() => window.open(detailShift.brief_url, '_blank', 'noopener,noreferrer')}
                        style={{ width: '100%', fontSize: 13, fontWeight: 700, color: 'var(--accent)', background: 'rgba(var(--accent-rgb),0.07)', border: '1px solid rgba(var(--accent-rgb),0.25)', borderRadius: 8, padding: '9px 14px', cursor: 'pointer', marginBottom: 4 }}
                      >
                        📄 Read Event Brief
                      </button>
                    )}
                  </>
                )}

                {(detailShift.location || detailShift.street_address) && (
                  <button
                    className="btn btn-primary"
                    style={{ marginTop: 12 }}
                    onClick={() => {
                      const address = [detailShift.street_address, detailShift.location].filter(Boolean).join(', ');
                      window.open(`https://maps.google.com?q=${encodeURIComponent(address)}`);
                    }}
                  >
                    Get me there →
                  </button>
                )}

                <button
                  onClick={closeDetail}
                  style={{ width: '100%', marginTop: 10, padding: '12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                >
                  Back to Schedule
                </button>

                <div style={{ height: 'env(safe-area-inset-bottom)' }} />
              </div>
            </div>
          </div>
        )}

        {/* Check-in confirmation modal */}
        {checkinModal && (
          <div onClick={e => { if (e.target === e.currentTarget) setCheckinModal(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 100, padding: '0 0 env(safe-area-inset-bottom)' }}>
            <div style={{ background: 'var(--bg-card)', borderRadius: '16px 16px 0 0', padding: '28px 24px 32px', width: '100%', maxWidth: 430, boxShadow: '0 -4px 30px rgba(0,0,0,0.2)' }}>
              <div style={{ width: 36, height: 4, background: 'var(--border)', borderRadius: 2, margin: '0 auto 24px' }} />
              <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--text-primary)', marginBottom: 6 }}>Confirm your attendance</div>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 6, lineHeight: 1.5 }}>{checkinModal.title}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
                {formatDate(checkinModal.date)} · {checkinModal.time_start} – {checkinModal.time_end}
              </div>
              <button
                onClick={confirmCheckin}
                disabled={checkinLoading}
                style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10 }}
              >
                {checkinLoading ? 'Confirming...' : "Yes, I'll be there"}
              </button>
              <button
                onClick={() => setCheckinModal(null)}
                style={{ width: '100%', padding: '13px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

      </div>
      <BottomNav />
    </div>
  );
}
